import classNames from 'classnames';
import type { HTMLAttributeAnchorTarget } from 'react';
import type AdminLink from '../../../../../types/AdminLink';
import type { CurrentUserDto } from '../../../../../types/UserDtos';
import { BASE, SETTINGS_BILLING } from '../../../../routes';
import { ThumbCard } from '../../../components/Cards';
import Ribbon from '../../../components/Cards/Ribbon';
import Link from '../../../components/Link';
import { hasBasicHr, isManager } from '../../../services/User';
import styles from './style.m.less';

const STATUS_CLASSES = {
    WARNING: styles.hasWarning,
    ERROR: styles.hasError,
    SUGGESTION: styles.hasSuggestion,
};

const getIconSrc = (iconName: string) => {
    try {
        return require(`./svg/${iconName}.svg`);
    } catch {
        return require(`./svg/default.svg`);
    }
};

function AdminLinkCompoundIcon({ icons }: { icons: string[] }) {
    'use memo';
    return (
        <div className={styles.compoundIcon}>
            {icons.map(i => (
                <img key={i} className={styles.compoundIconImage} src={getIconSrc(i)} alt="" />
            ))}
        </div>
    );
}

function AdminLinkIcon({ icon }: { icon: string }) {
    'use memo';
    return <img className={styles.image} src={getIconSrc(icon)} alt="" />;
}

type Props = AdminLink & {
    target?: HTMLAttributeAnchorTarget;
    currentUser?: CurrentUserDto;
};

export default function AdminLinkButton({
    name,
    url,
    tooltip,
    enabled,
    status,
    icon,
    className,
    target,
    currentUser = undefined,
}: Props) {
    'use memo';
    const linkClasses = classNames(styles.card, className, enabled && status && STATUS_CLASSES[status]);

    const isBillingButton = url === BASE + SETTINGS_BILLING;
    const showBuyRibbon = isBillingButton && currentUser && isUserToPurchase(currentUser);
    const card = (
        <ThumbCard isEnabled={enabled} className={linkClasses} isCompact={true}>
            {showBuyRibbon && <Ribbon text="Buy Here" color="BLUE" />}
            {Array.isArray(icon) && <AdminLinkCompoundIcon icons={icon} />}
            {!Array.isArray(icon) && <AdminLinkIcon icon={icon} />}
            <div>{name}</div>
        </ThumbCard>
    );

    return enabled ? (
        <Link href={url} className={styles.gridItem} hasTextStyle={false} data-tooltip={tooltip} target={target}>
            {card}
        </Link>
    ) : (
        <div className={styles.gridItem} data-tooltip={tooltip}>
            {card}
        </div>
    );
}

function isUserToPurchase(currentUser: CurrentUserDto) {
    if (currentUser) {
        const {
            company: {
                payment: { plan, userCount, freeLimit },
            },
        } = currentUser;

        return (hasBasicHr(currentUser) || isCEO(currentUser)) && plan === 0 && userCount > freeLimit;
    }
    return false;
}

function isCEO(user: CurrentUserDto) {
    return !user.isGuest && isManager(user) && !user.managerId;
}
